import React, { useState, useRef } from 'react';

const HolidayCalculatorSection = () => {
  const [futureAmount, setFutureAmount] = useState(500000);
  const [investmentHorizon, setInvestmentHorizon] = useState(3);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState(null);
  
  const resultRef = useRef(null);

  const calculateVacation = () => {
    // Calculate real return rate (adjusted for inflation)
    const realReturnRate = ((1 + expectedReturn / 100) / (1 + inflationRate / 100) - 1) * 100;
    
    // Monthly return rate
    const monthlyReturnRate = expectedReturn / 100 / 12;
    const totalMonths = investmentHorizon * 12;
    
    // Calculate monthly SIP required using PMT formula
    // FV = PMT * [((1 + r)^n - 1) / r]
    // PMT = FV * r / ((1 + r)^n - 1)
    let monthlySIPRequired = 0;
    if (totalMonths > 0) {
      monthlySIPRequired = futureAmount * monthlyReturnRate / (Math.pow(1 + monthlyReturnRate, totalMonths) - 1);
    }
    
    // Calculate total investment
    const totalInvestment = monthlySIPRequired * totalMonths;
    
    // Calculate wealth gain
    const wealthGain = futureAmount - totalInvestment;
    
    // Calculate future value adjusted for inflation
    const realFutureValue = futureAmount / Math.pow(1 + inflationRate / 100, investmentHorizon);
    
    // Calculate inflation adjusted cost
    const inflationAdjustedCost = futureAmount / Math.pow(1 + inflationRate / 100, investmentHorizon);
    
    const calculatedResult = {
      monthlySIPRequired: Math.round(monthlySIPRequired),
      totalInvestment: Math.round(totalInvestment),
      wealthGain: Math.round(wealthGain),
      realFutureValue: Math.round(realFutureValue),
      inflationAdjustedCost: Math.round(inflationAdjustedCost),
      realReturnRate: realReturnRate.toFixed(2)
    };
    
    setResult(calculatedResult);
    
    // Auto-scroll to result section after a short delay
    setTimeout(() => {
      if (resultRef.current) {
        resultRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const styles = {
    vacationCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0',
      padding: '1rem',
      backgroundColor: '#f0f9ff',
      borderRadius: '8px',
      border: '1px solid #bae6fd'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    // Result Section Styles
    resultSection: {
      marginTop: '3rem',
      padding: '0',
      backgroundColor: 'transparent'
    },
    resultHeader: {
      backgroundColor: '#c2e0f4',
      padding: '1rem 2rem',
      borderRadius: '8px 8px 0 0',
      border: '1px solid #a5c9dd',
      borderBottom: 'none'
    },
    resultHeaderText: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0'
    },
    resultContent: {
      backgroundColor: '#f9fafb',
      padding: '1.5rem 2rem',
      border: '1px solid #d1d5db',
      borderRadius: '0 0 8px 8px',
      fontSize: '0.95rem',
      color: '#374151',
      lineHeight: '1.6'
    },
    highlightText: {
      color: '#dc2626',
      fontWeight: '600'
    },
    recalculateButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #d1d5db',
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontSize: '0.9rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginTop: '1rem'
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `${(amount / 100000).toFixed(1)} L`;
    } else {
      return amount.toLocaleString();
    }
  };

  return (
    <>
      <style>{`
        .vacation-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .vacation-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .vacation-calculate-btn:hover {
          background-color: #dc2626 !important;
        }
        
        .recalculate-btn:hover {
          background-color: #e5e7eb !important;
          border-color: #9ca3af !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .vacation-result-content {
            padding: 1rem !important;
            font-size: 0.9rem !important;
          }
        }
      `}</style>
      
      <div style={styles.vacationCalculatorContent}>
        <div style={styles.description}>
          This calculator will calculate the amount you need to save monthly to meet your investment goal.
        </div>
        
        {/* Future Amount Required */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Future amount required (Rs):
            <input
              type="number"
              min="500000"
              max="200000000"
              step="500000"
              value={futureAmount}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setFutureAmount('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setFutureAmount(Math.max(500000, Math.min(200000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setFutureAmount(500000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter amount"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="500000"
              max="200000000"
              step="500000"
              value={futureAmount}
              onChange={(e) => setFutureAmount(parseInt(e.target.value))}
              style={styles.slider}
              className="vacation-slider"
            />
            <div style={styles.sliderLabels}>
              <span>500,000</span>
              <span>2,87,5,000</span>
              <span>5,25,0,000</span>
              <span>7,62,5,000</span>
              <span>10,00,0,000</span>
            </div>
          </div>
        </div>

        {/* Investment Horizon */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Investment horizon (years)
            <input
              type="number"
              min="1"
              max="30"
              step="1"
              value={investmentHorizon}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInvestmentHorizon('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setInvestmentHorizon(Math.max(1, Math.min(30, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setInvestmentHorizon(3);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter years"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="30"
              step="1"
              value={investmentHorizon}
              onChange={(e) => setInvestmentHorizon(parseInt(e.target.value))}
              style={styles.slider}
              className="vacation-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>5</span>
              <span>8</span>
              <span>10</span>
              <span>13</span>
              <span>16</span>
              <span>19</span>
              <span>22</span>
              <span>24</span>
              <span>27</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Annual Return Expected */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Annual return you expect to earn on investment (%):
            <input
              type="number"
              min="5"
              max="30"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExpectedReturn('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setExpectedReturn(Math.max(5, Math.min(30, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setExpectedReturn(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="5"
              max="30"
              step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(parseFloat(e.target.value))}
              style={styles.slider}
              className="vacation-slider"
            />
            <div style={styles.sliderLabels}>
              <span>5</span>
              <span>10.2</span>
              <span>12.4</span>
              <span>14.6</span>
              <span>16.8</span>
              <span>19</span>
              <span>21.2</span>
              <span>23.4</span>
              <span>25.6</span>
              <span>27.8</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Inflation Rate */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Inflation (%):
            <input
              type="number"
              min="3"
              max="15"
              step="0.5"
              value={inflationRate}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInflationRate('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setInflationRate(Math.max(3, Math.min(15, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setInflationRate(6);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="3"
              max="15"
              step="0.5"
              value={inflationRate}
              onChange={(e) => setInflationRate(parseFloat(e.target.value))}
              style={styles.slider}
              className="vacation-slider"
            />
            <div style={styles.sliderLabels}>
              <span>3</span>
              <span>4.2</span>
              <span>5.4</span>
              <span>6.6</span>
              <span>7.8</span>
              <span>9</span>
              <span>10.2</span>
              <span>11.4</span>
              <span>12.6</span>
              <span>13.8</span>
              <span>15</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="vacation-calculate-btn"
          onClick={calculateVacation}
        >
          Calculate
        </button>

        {/* Results Section */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            <div style={styles.resultHeader}>
              <h3 style={styles.resultHeaderText}>Result</h3>
            </div>
            
            <div style={styles.resultContent} className="vacation-result-content">
              Inflation adjusted cost will be{' '}
              <span style={styles.highlightText}>
                Rs.{result.inflationAdjustedCost.toLocaleString()}/-
              </span>
              . You need to invest{' '}
              <span style={styles.highlightText}>
                Rs.{result.monthlySIPRequired.toLocaleString()}/-
              </span>
              {' '}per month to fulfill your goal.
            </div>
            
            <button
              style={styles.recalculateButton}
              className="recalculate-btn"
              onClick={calculateVacation}
            >
              Re-Calculate
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default HolidayCalculatorSection;